import { cssPerLogical } from "../../ui/kit/measure.ts";
import type { SlotRect } from "../hud/slots.ts";

/**
 * Where a bag slot, a wallet mark or the carry number is on the page (P2.8-T11, P2.8-T67 and P2.8-T53), for its tooltip's place: the window's
 * content scrolls and the window moves, so a slot's or a mark's place is read from the page rather than worked out
 * from the window's box.
 */

/**
 * The anchor a bag slot's tooltip hangs from, as its `data-tip` attribute.
 *
 * @param slot The bag slot.
 * @returns `bag:<slot>`.
 * @example
 * bagTipAnchor(4);
 * // "bag:4"
 */
export function bagTipAnchor(slot: number): string {
  return `bag:${slot}`;
}

/**
 * A bag slot's rectangle on the page, in logical pixels from the HUD layer's top left.
 *
 * @param slot The bag slot.
 * @returns The rectangle, or null where there is no document, no such slot drawn, or no layer around it.
 */
export function measureBagSlot(slot: number): SlotRect | null {
  return measureTipAnchor(bagTipAnchor(slot));
}

/**
 * The rectangle on the page of the element a tooltip hangs from, a bag slot or a wallet mark (P2.8-T67), in logical
 * pixels from the HUD layer's top left.
 *
 * @param anchor The element's `data-tip` attribute, as `bag:4`, `wallet:1` or `carry:0`.
 * @returns The rectangle, or null where there is no document, no such element drawn, or no layer around it.
 * @example
 * measureTipAnchor("wallet:1");
 * // { left: 420, top: 300, width: 30, height: 16 }
 */
export function measureTipAnchor(anchor: string): SlotRect | null {
  if (typeof document === "undefined") {
    return null;
  }
  const element = document.querySelector<HTMLElement>(`[data-tip="${anchor}"]`);
  const layer = element?.closest<HTMLElement>("[data-hud='layer']") ?? null;
  const px = layer === null ? null : cssPerLogical(layer);
  if (element === null || layer === null || px === null) {
    return null;
  }
  const rect = element.getBoundingClientRect();
  const origin = layer.getBoundingClientRect();
  return { left: Math.round((rect.left - origin.left) / px), top: Math.round((rect.top - origin.top) / px), width: Math.round(rect.width / px), height: Math.round(rect.height / px) };
}
